import { getAllStoreTypeForSelect } from 'pages/Componets/API_Modal/StoreTypeAPIModal';
import notistack from 'pages/Core/Notistack';
import React, {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle
} from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { assignToStore } from '../services/po.stock.service';
import { HttpStatus } from 'pages/Core';

interface AssignToStoreProp {
    reloadDataSource?: () => void;
}

export interface AssignToStoreRef {
    open: (selectedProducts: any[]) => void;
    close: () => void;
}

interface AssignFormValues {
    storeId: string;
}

const AssignToStore = forwardRef<AssignToStoreRef, AssignToStoreProp>(
    ({ reloadDataSource }, ref) => {

        const [isOpen, setIsOpen] = useState(false);
        const [storeOptions, setStoreOptions] = useState<any[]>([]);
        const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
        const [loading, setLoading] = useState(false);
        const [submitting, setSubmitting] = useState(false);

        const {
            control,
            handleSubmit,
            reset,
            formState: { errors }
        } = useForm<AssignFormValues>({
            defaultValues: {
                storeId: ""
            }
        });

        // Expose methods to parent
        useImperativeHandle(ref, () => ({
            open: (products: any[]) => {
                setSelectedProducts(products);
                setIsOpen(true);
            },
            close: handleClose
        }));

        const handleClose = () => {
            setIsOpen(false);
            setSelectedProducts([]);
            reset();
        };

        const storeList = async () => {
            try {
                setLoading(true);
                const response = await getAllStoreTypeForSelect();

                const options =
                    response.data?.map((store: any) => ({
                        value: store.value,
                        label: store.text
                    })) || [];

                setStoreOptions(options);
            } catch (error: any) {
                notistack.error(error?.message || 'Failed to load stores');
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            if (isOpen) {
                storeList();
            }
        }, [isOpen]);

        const onSubmit = async (data: AssignFormValues) => {
            if (!selectedProducts.length) {
                return notistack.warning('No products selected');
            }

            try {
                setSubmitting(true);

                const productIds = selectedProducts.map((p: any) => p.id);
                const payload = {
                    productIds,
                    ...data
                }

                const response = await assignToStore(payload)

                if (response.status === HttpStatus.Success) {
                    notistack.notifyFromApi(response)
                    reloadDataSource?.();
                    handleClose();
                } else {
                    notistack.notifyFromApi(response)
                }
            } catch (error: any) {
                notistack.error(error?.message || 'Failed to assign products');
            } finally {
                setSubmitting(false);
            }
        };

        return (
            <Modal
                show={isOpen}
                onHide={handleClose}
                backdrop="static"
                dialogClassName="modal-dialog-centered"
            >
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Assign to Store</Modal.Title>
                    </Modal.Header>

                    <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Select Store <span className="text-danger">*</span>
                            </Form.Label>

                            <Controller
                                name="storeId"
                                control={control}
                                rules={{ required: "Store is required" }}
                                render={({ field }) => (
                                    <Select
                                        options={storeOptions}
                                        isLoading={loading}
                                        placeholder="-- Select Store --"
                                        value={
                                            storeOptions.find(
                                                (opt: any) => opt.value === field.value
                                            ) || null
                                        }
                                        onChange={(val: any) =>
                                            field.onChange(val?.value || "")
                                        }
                                        isClearable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        styles={{
                                            container: (base: any) => ({
                                                ...base,
                                                width: "100%"
                                            }),
                                            control: (base: any) => ({
                                                ...base,
                                                width: "100%"
                                            })
                                        }}
                                        menuPosition="fixed"
                                    />
                                )}
                            />

                            {errors.storeId && (
                                <small className="text-danger">
                                    {errors.storeId.message}
                                </small>
                            )}
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            className="btn add-btn me-2"
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            className="btn submit-btn ms-2"
                            disabled={submitting}
                        >
                            {submitting ? 'Assigning...' : 'Assign to Store'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }
);

export default AssignToStore;